// src/Dashboard/pages/teacher/Attendance/hooks/useAttendanceDashboard.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { getDojos } from "../../../../../services/dojoServices";
import { getGroupsList } from "../../../../../services/groupServices";
import {
  getAllAdherentsListWithGroup,
  getBirthdayAdherents,
} from "../../../../../services/adherentServices";
import { getSettings } from "../../../../../services/settingsServices";
import {
  getAllAttendances,
  countAttendance,
  deleteAllAttendances,
  setAttendance as apiSetAttendance,
  clearAttendance as apiClearAttendance,
} from "../../../../../services/attendanceServices";

import {
  STOPPED_STATUSES,
  buildDailyAttendanceMap,
  getLocalDateInput,
  getSeasonNeighbors,
  getGroupsLabel,
  hasStoppedStatus,
  normalizeAttendanceFilters,
  sortAdherentsByName,
} from "../utils/attendanceUtils";

const DEFAULT_FILTERS = {
  search: "",
  selectedDojo: "",
  selectedSeason: "",
  filterGroup: "",
  statutInscription: "",
  selectedDate: getLocalDateInput(),
  hideStopped: true,
  sortAssiduity: false,
  onlyBirthday: false,
  onlyBlessed: false,
  onlyPresent: false,
  onlyAbsent: false,
};

// function isActiveGroup(group) {
//   return (
//     group?.is_active === true ||
//     group?.is_active === 1 ||
//     group?.is_active === "1" ||
//     group?.is_active === "true"
//   );
// }

export function useAttendanceDashboard() {
  const [dojos, setDojos] = useState([]);
  const [allGroups, setAllGroups] = useState([]);

  const [adherents, setAdherents] = useState([]);
  const [birthdayAdherents, setBirthdayAdherents] = useState([]);

  const [seasons, setSeasons] = useState([]);
  const [filtersState, setFiltersState] = useState(DEFAULT_FILTERS);

  const [attendance, setAttendance] = useState({});
  const attendanceRef = useRef(attendance);

  const [allAttendancesCache, setAllAttendancesCache] = useState([]);
  const [assiduityInfo, setAssiduityInfo] = useState({});

  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState(new Set());

  const filters = useMemo(
    () => normalizeAttendanceFilters(filtersState),
    [filtersState],
  );

  const setFilters = useCallback((updater) => {
    setFiltersState((previous) => {
      const previousNormalized = normalizeAttendanceFilters(previous);

      const candidate =
        typeof updater === "function"
          ? updater(previousNormalized)
          : {
              ...previousNormalized,
              ...updater,
            };

      const nextNormalized = normalizeAttendanceFilters(candidate);

      const dojoChanged =
        String(previousNormalized.selectedDojo || "") !==
        String(nextNormalized.selectedDojo || "");

      if (dojoChanged) {
        return normalizeAttendanceFilters({
          ...nextNormalized,
          filterGroup: "",
        });
      }

      return nextNormalized;
    });
  }, []);

  const groups = useMemo(() => {
  const list = Array.isArray(allGroups) ? allGroups : [];

  if (!filters.selectedDojo) {
    return list;
  }

  return list.filter(
    (group) => String(group.dojo_id) === String(filters.selectedDojo),
  );
}, [allGroups, filters.selectedDojo]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        settingsData,
        dojoList,
        groupList,
        adherentList,
        birthdayList,
        attendanceList,
      ] = await Promise.all([
        getSettings(),
        getDojos(),
        getGroupsList(),
        getAllAdherentsListWithGroup(),
        getBirthdayAdherents(),
        getAllAttendances(),
      ]);

      const settings = Array.isArray(settingsData)
        ? settingsData[0]
        : settingsData;

      const safeDojos = Array.isArray(dojoList) ? dojoList : [];
      const safeGroups = Array.isArray(groupList) ? groupList : [];
      const safeAdherents = Array.isArray(adherentList) ? adherentList : [];
      const safeBirthdays = Array.isArray(birthdayList) ? birthdayList : [];
      const safeAttendances = Array.isArray(attendanceList)
        ? attendanceList
        : [];

      setDojos(safeDojos);
      setAllGroups(safeGroups);
      setAdherents(safeAdherents);
      setBirthdayAdherents(safeBirthdays);
      setAllAttendancesCache(safeAttendances);

      const activeSeason = settings?.saison_active || "";
      if (activeSeason) {
        const { prev, next } = getSeasonNeighbors(activeSeason);
        setSeasons([prev, activeSeason, next].filter(Boolean));
      }

      setFiltersState((previous) => {
        const normalized = normalizeAttendanceFilters(previous);

        const autoDojo =
          safeDojos.length === 1
            ? String(safeDojos[0].id)
            : normalized.selectedDojo;

        return normalizeAttendanceFilters({
          ...normalized,
          selectedDojo: autoDojo,
          selectedSeason: normalized.selectedSeason || activeSeason,
          selectedDate: normalized.selectedDate || getLocalDateInput(),
        });
      });

      const ids = safeAdherents.map((adherent) => adherent.id);
      const initialMap = buildDailyAttendanceMap(
        filters.selectedDate || getLocalDateInput(),
        ids,
        safeAttendances,
      );

      setAttendance(initialMap);
      attendanceRef.current = initialMap;
    } catch (error) {
      console.error("Erreur chargement présences :", error);
      toast.error("Erreur de chargement des présences.");
    } finally {
      setLoading(false);
    }
  }, [filters.selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    attendanceRef.current = attendance;
  }, [attendance]);

  const birthdaySet = useMemo(
    () => new Set((birthdayAdherents || []).map((adherent) => adherent.id)),
    [birthdayAdherents],
  );

  const allowedGroupNames = useMemo(
    () => new Set((groups || []).map((group) => group.nom)),
    [groups],
  );

  const filteredAdherents = useMemo(() => {
    const query = String(filters.search || "")
      .trim()
      .toLowerCase();

    let list = (adherents || []).filter((adherent) => {
      const groupsLabel = getGroupsLabel(adherent);

      const matchesDojo =
        !filters.selectedDojo ||
        (Array.isArray(adherent?.Groups) &&
          adherent.Groups.some((group) => allowedGroupNames.has(group.nom)));

      const matchesGroup =
        !filters.filterGroup ||
        (Array.isArray(adherent?.Groups) &&
          adherent.Groups.some((group) => group.nom === filters.filterGroup));

      const matchesSeason =
        !filters.selectedSeason ||
        adherent?.saison_active === filters.selectedSeason;

      const matchesStatus =
        !filters.statutInscription ||
        adherent?.statut_inscription === filters.statutInscription;

      const matchesStopped =
        !filters.hideStopped || !hasStoppedStatus(adherent);

      const matchesBirthday =
        !filters.onlyBirthday || birthdaySet.has(adherent.id);

      const matchesBlessed = !filters.onlyBlessed || !!adherent?.is_blessed;

      const attendanceStatus = attendance?.[adherent.id] ?? 0;

      const matchesPresent = !filters.onlyPresent || attendanceStatus === 1;
      const matchesAbsent = !filters.onlyAbsent || attendanceStatus === 2;

      const matchesSearch =
        !query ||
        [
          adherent?.nom,
          adherent?.prenom,
          adherent?.licence_number,
          adherent?.statut_inscription,
          groupsLabel,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

      return (
        matchesDojo &&
        matchesGroup &&
        matchesSeason &&
        matchesStatus &&
        matchesStopped &&
        matchesBirthday &&
        matchesBlessed &&
        matchesPresent &&
        matchesAbsent &&
        matchesSearch
      );
    });

    if (filters.sortAssiduity) {
      list = list.slice().sort((a, b) => {
        const valueA = Number(assiduityInfo[a.id]?.assiduity || 0);
        const valueB = Number(assiduityInfo[b.id]?.assiduity || 0);

        return valueB - valueA;
      });
    } else {
      list = sortAdherentsByName(list);
    }

    return list;
  }, [
    adherents,
    filters,
    allowedGroupNames,
    birthdaySet,
    attendance,
    assiduityInfo,
  ]);

  useEffect(() => {
    if (!allAttendancesCache) return;

    const ids = filteredAdherents.map((adherent) => adherent.id);

    const map = buildDailyAttendanceMap(
      filters.selectedDate,
      ids,
      allAttendancesCache,
    );

    setAttendance((previous) => {
      const next = { ...previous };

      ids.forEach((id) => {
        next[id] = map[id] ?? 0;
      });

      attendanceRef.current = next;

      return next;
    });
  }, [filters.selectedDate, filteredAdherents, allAttendancesCache]);

  const loadAssiduity = useCallback(
    async (ids = []) => {
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)));

      const idsToLoad = uniqueIds.filter((id) => !assiduityInfo[id]);

      if (!idsToLoad.length) return;

      try {
        const results = await Promise.all(
          idsToLoad.map((id) =>
            countAttendance(id)
              .then((data) => ({ id, data }))
              .catch(() => ({ id, data: null })),
          ),
        );

        setAssiduityInfo((previous) => {
          const next = { ...previous };

          results.forEach(({ id, data }) => {
            if (data) next[id] = data;
          });

          return next;
        });
      } catch {
        // volontairement silencieux
      }
    },
    [assiduityInfo],
  );

  const setSavingForId = useCallback((adherentId, value) => {
    setSavingIds((previous) => {
      const next = new Set(previous);

      if (value) next.add(String(adherentId));
      else next.delete(String(adherentId));

      return next;
    });
  }, []);

  const handleToggleAttendance = useCallback(
    async (adherentId) => {
      if (loading || savingIds.has(String(adherentId))) return;

      const previousValue = attendanceRef.current[adherentId] ?? 0;
      const nextValue = previousValue === 0 ? 1 : previousValue === 1 ? 2 : 0;

      setAttendance((previous) => {
        const next = {
          ...previous,
          [adherentId]: nextValue,
        };

        attendanceRef.current = next;

        return next;
      });

      const dateISO = String(filters.selectedDate).slice(0, 10);

      try {
        setSavingForId(adherentId, true);

        if (nextValue === 1) {
          await apiSetAttendance(adherentId, dateISO, true);

          setAllAttendancesCache((previous) => {
            const others = (previous || []).filter(
              (item) =>
                !(
                  Number(item.adherent_id) === Number(adherentId) &&
                  String(item.date_presence).slice(0, 10) === dateISO
                ),
            );

            return [
              ...others,
              {
                adherent_id: Number(adherentId),
                presence: true,
                date_presence: dateISO,
              },
            ];
          });
        } else if (nextValue === 2) {
          await apiSetAttendance(adherentId, dateISO, false);

          setAllAttendancesCache((previous) => {
            const others = (previous || []).filter(
              (item) =>
                !(
                  Number(item.adherent_id) === Number(adherentId) &&
                  String(item.date_presence).slice(0, 10) === dateISO
                ),
            );

            return [
              ...others,
              {
                adherent_id: Number(adherentId),
                presence: false,
                date_presence: dateISO,
              },
            ];
          });
        } else {
          await apiClearAttendance(adherentId, dateISO);

          setAllAttendancesCache((previous) =>
            (previous || []).filter(
              (item) =>
                !(
                  Number(item.adherent_id) === Number(adherentId) &&
                  String(item.date_presence).slice(0, 10) === dateISO
                ),
            ),
          );
        }
      } catch (error) {
        console.error("Erreur mise à jour présence :", error);

        setAttendance((previous) => {
          const next = {
            ...previous,
            [adherentId]: previousValue,
          };

          attendanceRef.current = next;

          return next;
        });

        toast.error("Échec de la mise à jour de la présence.");
      } finally {
        setSavingForId(adherentId, false);
      }
    },
    [loading, savingIds, filters.selectedDate, setSavingForId],
  );

  const handleDeleteAllAttendances = useCallback(async () => {
    if (
      !window.confirm(
        "Supprimer tous les appels enregistrés ? Cette action est irréversible.",
      )
    ) {
      return;
    }

    try {
      await deleteAllAttendances();

      setAllAttendancesCache([]);
      setAttendance({});
      attendanceRef.current = {};

      toast.success("Tous les appels ont été supprimés.");
    } catch (error) {
      console.error("Erreur suppression appels :", error);
      toast.error("Erreur lors de la suppression des appels.");
    }
  }, []);

  const stats = useMemo(() => {
    const ids = new Set(filteredAdherents.map((adherent) => adherent.id));

    let present = 0;
    let absent = 0;
    let neutral = 0;

    filteredAdherents.forEach((adherent) => {
      const value = attendance?.[adherent.id] ?? 0;

      if (value === 1) present += 1;
      else if (value === 2) absent += 1;
      else neutral += 1;
    });

    const total = ids.size;
    const filled = present + absent;
    const completion = total > 0 ? Math.round((filled / total) * 100) : 0;

    const birthday = filteredAdherents.filter((adherent) =>
      birthdaySet.has(adherent.id),
    ).length;

    const blessed = filteredAdherents.filter(
      (adherent) => !!adherent?.is_blessed,
    ).length;

    const stopped = filteredAdherents.filter((adherent) =>
      STOPPED_STATUSES.includes(adherent?.statut_inscription),
    ).length;

    return {
      total,
      present,
      absent,
      neutral,
      filled,
      completion,
      birthday,
      blessed,
      stopped,
    };
  }, [filteredAdherents, attendance, birthdaySet]);

  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  return {
    adherents,
    filteredAdherents,
    stats,

    filters,
    setFilters,

    seasons,
    dojos,
    groups,
    allGroups,

    attendance,
    assiduityInfo,
    birthdaySet,

    loading,
    savingIds,

    refresh,
    handleToggleAttendance,
    handleDeleteAllAttendances,
    loadAssiduity,
  };
}

export default useAttendanceDashboard;
